import PropTypes from 'prop-types';

import { OpportunityLogo } from '@molecules/Opportunity';
import { Image, Text } from '@ui/atoms';
import Button from '@ui/atoms/design-system/Button';

import { buttonSizes, buttonTypes } from '@ui/atoms/utils/enums';
import { cn } from '@ui/atoms/utils/utils';

import BackButton from '@components/BondDirectoryFeature/feature/BondDetails/components/OpportunityDetailsHeader/BackButton';
import DetailValue from '@components/BondDirectoryFeature/feature/BondDetails/components/OpportunityDetailsHeader/DetailValue';
import ToolTipOrText from '@components/BondDirectoryFeature/feature/BondDetails/components/OpportunityDetailsHeader/ToolTipOrText';
import ListingWrapper from '@components/OpportunityListing/components/ListingWrapper';
import MarketTimings from '@components/BondDirectoryFeature/feature/BondDetails/components/MarketTimings';
import { useState } from 'react';
import { copyToClipboard } from '@ui/atoms/utils/helper';
import { useStoreSelector } from '@ui/hooks/useStoreSelector';
import useOpportunitiesStore from '@components/OpportunityListing/store/useOpportunitiesStore';
import AskMeBanner from '@components/GenAi/Banners/AskMeBanner';

const OpportunityDetailsHeader = ({
    title,
    tags,
    details,
    logo,
    onBack,
    showPaymentSchedule,
    onViewPaymentSchedule,
    viewPaymentScheduleText,
    className,
    wrapperClassName,
    marketOpenTime,
    children,
    canUseAiBot,
    ...props
}) => {
    const [copySuccess, setCopySuccess] = useState('');
    const onCopy = (value) => {
        copyToClipboard(value, setCopySuccess);
    };
    const { isAuthorized } = useStoreSelector(
        useOpportunitiesStore,
        (state) => ({
            isAuthorized: state.isAuthorized,
        })
    );

    return (
        <ListingWrapper
            className={cn(
                "bg-[url('/images/opportunities/ListingBackground.svg')] w-full bg-cover bg-center",
                className
            )}
            wrapperClassName={cn(
                'px-4 py-6 xl:pb-8 xl:pt-5 xl:px-6 max-w-[1360px]',
                wrapperClassName
            )}
            tag="header"
        >
            <div
                className={cn('flex flex-col gap-[1.25rem] xl:max-w-[821px]', {
                    'px-4 xl:px-0': wrapperClassName,
                })}
            >
                <BackButton
                    handleGoBack={onBack}
                    textContent="Back"
                    imageSrc="/icons/ChevronLeftWhite.svg"
                    imageAlt="back"
                    imageClassName="w-4 h-4"
                    className={isAuthorized ? 'hidden md:inline-block' : ''}
                />
                <div className="text-basicWhite max-w-[1360px] mx-auto w-full grid gap-6">
                    <div className="grid grid-cols-[auto_1fr] gap-2 md:gap-4 items-start md:items-center">
                        {logo ? (
                            <div className="flex items-center w-12 h-12 md:w-18 md:h-18">
                                <OpportunityLogo
                                    alt={title}
                                    logo={logo}
                                    wrapperClass="w-12 h-12 md:w-18 md:h-18"
                                    className="object-contain w-12 h-12 md:w-18 md:h-18"
                                />
                            </div>
                        ) : null}
                        <div className="flex flex-col gap-2">
                            <Text
                                content={title}
                                className="md:h5-bold line-clamp-2 h6-semibold"
                            />
                            <div className="flex flex-wrap items-center gap-2">
                                {tags?.map((tag, index) => (
                                    <div
                                        key={index}
                                        className="px-2 py-1 rounded bg-basicWhite/10"
                                    >
                                        <Text
                                            content={tag}
                                            className="capitalize p6-medium md:p5-medium text-basicWhite whitespace-nowrap"
                                            htmlTag="p"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div
                        className={cn(' gap-2 md:gap-4 items-center', {
                            'grid xl:grid-cols-[auto_1fr]': logo,
                        })}
                    >
                        {logo ? (
                            <div
                                className={cn(
                                    'items-center hidden w-10 h-full xl:flex md:w-18'
                                )}
                            />
                        ) : (
                            <div className="hidden" />
                        )}
                        <div className="grid w-full grid-cols-2 gap-y-6 md:grid-cols-3 xl:grid-cols-[repeat(auto-fit,minmax(92px,max-content))] xl:gap-10">
                            {details?.map((detail, index) => (
                                <div
                                    key={index}
                                    className={cn(
                                        'flex flex-col gap-1',
                                        detail?.className
                                    )}
                                >
                                    <ToolTipOrText
                                        label={detail.label}
                                        tooltip={detail.tooltip}
                                    />
                                    <DetailValue
                                        value={detail.value}
                                        showCopy={detail.showCopy}
                                        copySuccess={copySuccess}
                                        onCopy={() => onCopy(detail.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    {showPaymentSchedule && (
                        <Button
                            buttonText={viewPaymentScheduleText}
                            buttonSize={buttonSizes.SMALL}
                            buttonType={buttonTypes.TERTIARY}
                            onClick={onViewPaymentSchedule}
                            className="xl:hidden"
                            widthClass="w-full mx-auto"
                            prefixComponent={
                                <Image
                                    src="/icons/EyePrimary.svg"
                                    alt="view repayment schedule"
                                    className="w-4 h-4"
                                />
                            }
                        />
                    )}
                    {marketOpenTime && (
                        <MarketTimings
                            bgColor="bg-basicWhite/10 md:hidden"
                            marketOpenTime={marketOpenTime?.value}
                        />
                    )}

                    {canUseAiBot ? <AskMeBanner wrapperClassName='rounded-xl md:flex lg:hidden' /> : null}
                </div>
            </div>
            {children}
        </ListingWrapper>
    );
};

OpportunityDetailsHeader.propTypes = {
    title: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string),
    details: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
                .isRequired,
            tooltip: PropTypes.string,
            showCopy: PropTypes.bool,
            className: PropTypes.string,
        })
    ),
    logo: PropTypes.string,
    onBack: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
    copySuccess: PropTypes.bool,
    showPaymentSchedule: PropTypes.bool,
    onViewPaymentSchedule: PropTypes.func,
    viewPaymentScheduleText: PropTypes.string,
    className: PropTypes.string,
    wrapperClassName: PropTypes.string,
};

export default OpportunityDetailsHeader;
